import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "hiddenInput", "dropdown"];

  static values = {
    bankAccounts: Array,
    transactionName: String,
    transactionSubject: String,
    transactionReference: String,
  };

  connect() {
    this.accounts = this.bankAccountsValue;
    this.selectedIndex = -1;
    this.isOpen = false;

    this.suggestions = this.computeSuggestions();

    // Pre-populate if a bank account is already selected
    const currentId = this.hiddenInputTarget.value;
    if (currentId) {
      const account = this.accounts.find(
        (a) => String(a.id) === String(currentId)
      );
      if (account) {
        this.inputTarget.value = account.displayName;
      }
    }

    this._onClickOutside = this.clickOutside.bind(this);
    document.addEventListener("click", this._onClickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside);
  }

  computeSuggestions() {
    const fields = [
      this.transactionNameValue,
      this.transactionSubjectValue,
      this.transactionReferenceValue,
    ].filter(Boolean);

    if (fields.length === 0) return [];

    const scored = this.accounts.map((account) => {
      let score = 0;

      for (const field of fields) {
        const lower = field.toLowerCase();

        // Match against name_matcher patterns (highest priority)
        if (account.nameMatcher) {
          const patterns = account.nameMatcher.split(",").map((p) => p.trim().toLowerCase());
          for (const pattern of patterns) {
            if (pattern && lower.includes(pattern)) {
              score += 100;
            }
          }
        }

        // Match against customer_reference
        if (
          account.customerReference &&
          lower.includes(account.customerReference.toLowerCase())
        ) {
          score += 80;
        }

        // Match against displayName
        if (lower.includes(account.displayName.toLowerCase())) {
          score += 50;
        }

        // Match against accountCompanyName
        if (
          account.accountCompanyName &&
          lower.includes(account.accountCompanyName.toLowerCase())
        ) {
          score += 50;
        }

        // Match against name
        if (account.name && lower.includes(account.name.toLowerCase())) {
          score += 30;
        }

        // Match against transactionNameMatcher patterns
        if (account.transactionNameMatcher) {
          const patterns = account.transactionNameMatcher.split(",").map((p) => p.trim().toLowerCase());
          for (const pattern of patterns) {
            if (pattern && lower.includes(pattern)) {
              score += 90;
            }
          }
        }

        // Match against pharmacy names
        if (account.pharmacyNames && lower.includes(account.pharmacyNames.toLowerCase())) {
          score += 40;
        }
      }

      return { account, score };
    });

    return scored
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((s) => s.account);
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim();
    this.renderDropdown(query);
    this.open();

    // Clear hidden field when typing
    this.hiddenInputTarget.value = "";
  }

  renderDropdown(query = "") {
    const items = [];

    if (query === "") {
      // Show suggestions first, then all accounts
      if (this.suggestions.length > 0) {
        items.push({ type: "header", label: "Suggested" });
        this.suggestions.forEach((a) => items.push({ type: "account", account: a }));
        items.push({ type: "header", label: "All accounts" });
      }
      this.accounts.forEach((a) => items.push({ type: "account", account: a }));
    } else {
      const filtered = this.accounts.filter((a) => {
        const searchFields = [
          a.displayName,
          a.name,
          a.nameMatcher,
          a.accountCompanyName,
          a.customerReference,
          a.transactionNameMatcher,
          a.pharmacyNames,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchFields.includes(query);
      });

      // Show matching suggestions at top
      const suggestedIds = new Set(this.suggestions.map((s) => s.id));
      const matchingSuggestions = filtered.filter((a) => suggestedIds.has(a.id));
      const rest = filtered.filter((a) => !suggestedIds.has(a.id));

      if (matchingSuggestions.length > 0) {
        items.push({ type: "header", label: "Suggested" });
        matchingSuggestions.forEach((a) => items.push({ type: "account", account: a }));
        if (rest.length > 0) {
          items.push({ type: "header", label: "All accounts" });
        }
      }
      rest.forEach((a) => items.push({ type: "account", account: a }));

      if (filtered.length === 0) {
        items.push({ type: "empty" });
      }
    }

    this.dropdownTarget.innerHTML = "";
    this.selectedIndex = -1;
    let accountIndex = 0;

    items.forEach((item) => {
      if (item.type === "header") {
        const li = document.createElement("li");
        li.className =
          "px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-100";
        li.textContent = item.label;
        this.dropdownTarget.appendChild(li);
      } else if (item.type === "empty") {
        const li = document.createElement("li");
        li.className = "px-3 py-2 text-sm text-gray-400";
        li.textContent = "No matches found";
        this.dropdownTarget.appendChild(li);
      } else {
        const li = document.createElement("li");
        li.className =
          "px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-blue-50";
        li.dataset.accountId = item.account.id;
        li.dataset.displayName = item.account.displayName;
        li.dataset.index = accountIndex;
        accountIndex++;

        const nameSpan = document.createElement("span");
        nameSpan.textContent = item.account.displayName;
        li.appendChild(nameSpan);

        if (item.account.customerReference) {
          const refSpan = document.createElement("span");
          refSpan.className = "ml-2 text-xs text-gray-400";
          refSpan.textContent = item.account.customerReference;
          li.appendChild(refSpan);
        }

        li.addEventListener("click", () => {
          this.selectAccount(item.account.id, item.account.displayName);
        });

        this.dropdownTarget.appendChild(li);
      }
    });
  }

  onKeydown(event) {
    if (!this.isOpen) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        this.filter();
        event.preventDefault();
      }
      return;
    }

    const selectableItems = this.dropdownTarget.querySelectorAll(
      "li[data-account-id]"
    );

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        selectableItems.length - 1
      );
      this.highlightItem(selectableItems);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.highlightItem(selectableItems);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (this.selectedIndex >= 0 && selectableItems[this.selectedIndex]) {
        const item = selectableItems[this.selectedIndex];
        this.selectAccount(item.dataset.accountId, item.dataset.displayName);
      }
    } else if (event.key === "Escape") {
      this.close();
    }
  }

  highlightItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-blue-50");
        item.scrollIntoView({ block: "nearest" });
      } else {
        item.classList.remove("bg-blue-50");
      }
    });
  }

  selectAccount(id, displayName) {
    this.hiddenInputTarget.value = id;
    this.inputTarget.value = displayName;
    this.close();
  }

  open() {
    if (!this.isOpen) {
      this.isOpen = true;
      this.dropdownTarget.classList.remove("hidden");
    }
  }

  close() {
    this.isOpen = false;
    this.selectedIndex = -1;
    this.dropdownTarget.classList.add("hidden");
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }
}
